<script setup lang="ts">
import { useCheckoutProgressStore } from '@stores/checkout/checkoutProgressStore';
import { useQuizStore } from '@stores/quiz';
import { computed } from 'vue';

const quizStore = useQuizStore();
const checkoutProgressStore = useCheckoutProgressStore();

const realCounter = computed(() => quizStore.steps.findIndex((step) => step.id === quizStore.currentStep.id));
const authIndex = computed(() => quizStore.steps.findIndex((step) => step.type === 'stepAuth'));

const cartIndex = computed(() => quizStore.steps.findIndex((step) => {
  return step.content[0].type === 'cartContent' || step.content[0].type === 'cartContentV2';
}));

// "-2" because the last step is "You have already completed this quiz"
const lastIndex = computed(() => quizStore.steps.length - 2);

const getProgress = (startIndex: number, endIndex: number) => {
  // "+ 1" is necessary for the progress to be visible at the first step
  const progress = ((startIndex + 1) / (endIndex + 1)) * 100;
  // Max value is 100
  return Math.min(progress, 100);
};

// "return -1" fixes a bug with a progress line that is not completely hidden
const firstPartProgress = computed(() => {
  if (quizStore.loading) {
    return -1;
  }
  return getProgress(realCounter.value, authIndex.value);
});

const secondPartProgress = computed(() => {
  if (quizStore.loading || realCounter.value <= authIndex.value) {
    return -1;
  }
  return getProgress(
    realCounter.value - authIndex.value,
    cartIndex.value - authIndex.value - 1,
  );
});

const thirdPartProgress = computed(() => {
  if (quizStore.loading || realCounter.value < cartIndex.value) {
    return -1;
  }
  if (realCounter.value > cartIndex.value) {
    return 100;
  }
  return getProgress(
    checkoutProgressStore.currentStepIndex,
    checkoutProgressStore.steps.length - 1,
  );
});

const fourthPartProgress = computed(() => {
  if (quizStore.loading || realCounter.value <= cartIndex.value) {
    return -1;
  }
  return getProgress(
    realCounter.value - cartIndex.value,
    lastIndex.value - cartIndex.value,
  );
});
</script>

<template>
  <div class="flex h-6 gap-6">
    <div
      class="relative h-full overflow-hidden rounded-full bg-primary-200"
      :class="realCounter <= authIndex ? 'w-full' : 'w-[20px]'"
    >
      <!-- "- 100" because we need to change translate value from -100 to 0 -->
      <div
        class="h-full w-full rounded-full bg-primary-500 duration-[0.6s] ease-out"
        :style="{
          transform: `translateX(${firstPartProgress - 100}%)`
        }"
      />
    </div>

    <div
      class="relative h-full overflow-hidden rounded-full bg-primary-200"
      :class="realCounter > authIndex && realCounter < cartIndex ? 'w-full' : 'w-[20px]'"
    >
      <div
        class="h-full w-full rounded-full bg-primary-500 duration-[0.6s] ease-out"
        :style="{
          transform: `translateX(${secondPartProgress - 100}%)`
        }"
      />
    </div>

    <div
      class="relative h-full overflow-hidden rounded-full bg-primary-200"
      :class="realCounter === cartIndex && !quizStore.loading ? 'w-full' : 'w-[20px]'"
    >
      <div
        class="h-full w-full rounded-full bg-primary-500 duration-[0.6s] ease-out"
        :style="{
          transform: `translateX(${thirdPartProgress - 100}%)`
        }"
      />
    </div>

    <div
      class="relative h-full overflow-hidden rounded-full bg-primary-200"
      :class="realCounter > cartIndex ? 'w-full' : 'w-[20px]'"
    >
      <div
        class="h-full w-full rounded-full bg-primary-500 duration-[0.6s] ease-out"
        :style="{
          transform: `translateX(${fourthPartProgress - 100}%)`
        }"
      />
    </div>
  </div>
</template>
